import React, { Key, useCallback, useState, useEffect } from 'react';
import { ListBox, ListBoxItem, Button, Popover, SelectValue, Select } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { OptionKeyValue, ValidationResult } from '@myriadgenetics/mgh-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import NotSure from '../not-sure';
import { notSure, getDisplayKey } from '../helpers';
import arrowDown from '../../../../assets/arrow-down.svg';
import './index.scss';
import { isSimonMedUrl } from '../../../../helpers/url';
import InputLabel from '../../../../elements/input-label';

interface Props {
  labelKey: string;
  isDisabled?: boolean;
  onOptionSelected: (selectedOption: OptionKeyValue) => void;
  placeholderKey: string;
  required: boolean;
  validationResult?: ValidationResult;
  selectedOptions: OptionKeyValue[];
  selectionOptions: OptionKeyValue[];
  icon?: IconDefinition;
  isChatbotStyle?: boolean;
}

const baseClass = 'native-select';

function NativeSelect({
  labelKey,
  onOptionSelected,
  placeholderKey,
  required,
  selectedOptions,
  selectionOptions,
  validationResult,
  isDisabled,
  isChatbotStyle,
  icon,
}: Props) {
  const { t } = useTranslation();
  const isInvalid = validationResult && !validationResult.isValid;
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectedOptionsValue =
    Array.isArray(selectedOptions) && selectedOptions.length > 0 ? (selectedOptions[0]?.optionValue as any) || selectedOptions[0] : '';

  const getDisplayValue = () => {
    if (typeof selectedOptionsValue === 'number') {
      return selectionOptions.find((o) => o.optionKey === selectedOptionsValue)?.optionValue;
    }

    return selectedOptionsValue;
  };

  const triggerChange = useCallback(
    (selection: Key) => {
      const userSelectedOption = selectionOptions.find((o) => o.optionKey.toString() === selection.toString());
      userSelectedOption && onOptionSelected(userSelectedOption);
    },
    [onOptionSelected, selectionOptions],
  );

  const optionContains = (value: string | number) => selectionOptions.some((o) => getDisplayKey(o) === value);

  const hasNotSure = () => optionContains(notSure);

  const filteredSelectionOptions = () => selectionOptions.filter((o) => getDisplayKey(o) !== notSure);

  const getSelectedKey = () => (selectedOptions.length > 0 ? selectedOptions[0]?.optionKey : undefined);

  // The RAC Popover on the RAC Select mispositions itself when resizing. Closing the Select solves the problem.
  const resize = () => {
    setIsSelectOpen(false);
  };

  useEffect(() => {
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    resize();
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setTimeout(
      () => {
        setIsSelectOpen(isOpen);
      },
      isOpen ? 0 : 100,
    );
  };

  return (
    <React.Fragment>
      <Select
        className={`
          ${baseClass} 
          ${isChatbotStyle ? `${baseClass}--myra` : ''}
          ${isSimonMedUrl() ? `${baseClass}--smi` : ''}
          ${baseClass}__select-input 
          ${!isInvalid && selectedOptions.length > 0 ? `${baseClass}__select-input--selected` : ''} 
          ${isInvalid && selectedOptions.length !== 0 ? `${baseClass}__select-input--error` : ''}`}
        isDisabled={isDisabled}
        onSelectionChange={triggerChange}
        onOpenChange={(isOpen: boolean) => handleOpenChange(isOpen)}
        isOpen={isSelectOpen}
        data-testid={`${t(labelKey)}-dropdown`}
        selectedKey={getSelectedKey()}
        isRequired={required}
        placeholder={t(placeholderKey)}
      >
        <InputLabel asterisk={required}>{t(labelKey)}</InputLabel>
        <Button>
          <SelectValue className="react-aria-SelectValue heading-small--regular">
            {icon && <FontAwesomeIcon className={`${baseClass}__person-icon`} icon={icon} />}
            {t(getDisplayValue()) || <span className={`${baseClass}__placeholder heading-small--regular"`}>{t(placeholderKey || labelKey)}</span>}
          </SelectValue>
          <span aria-label={`${t(labelKey)}-selected`} aria-hidden="true">
            <img className={`${baseClass}__icon`} src={arrowDown} alt="" />
          </span>
        </Button>
        <Popover
          className={`${baseClass}__popover ${isSimonMedUrl() ? `${baseClass}--smi__popover` : ''}`}
          placement="bottom"
          offset={12}
          crossOffset={-4}
          maxHeight={200}
        >
          <ListBox className={`${baseClass}__box`}>
            {filteredSelectionOptions().map((o, i) => (
              <ListBoxItem
                key={i}
                id={o.optionKey}
                className={`${baseClass}__box-item text-medium--regular ${isSimonMedUrl() ? `${baseClass}--smi__box-item` : ''}`}
                textValue={t(getDisplayKey(o))}
              >
                {t(getDisplayKey(o))}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </Select>
      {hasNotSure() && (
        <NotSure
          onAction={() => {
            const notSureOption = selectionOptions.find((o) => getDisplayKey(o) === notSure);
            notSureOption && onOptionSelected(notSureOption);
          }}
        />
      )}
    </React.Fragment>
  );
}

export default NativeSelect;
